import { useTranslations } from '@vocab/react';
import {
  Box,
  Checkbox,
  Heading,
  IconCritical,
  IconHelp,
  Inline,
  Secondary,
  Stack,
  Text,
  TextLink,
  TooltipRenderer,
} from 'braid-design-system';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import type { IntegrationRelationshipTypeEnum } from 'src/App/generated/graphql-types';
import type { Partner, RelationshipTypeCode } from 'src/types';
import { useIsAuNz } from 'src/utils/hooks/useContext';

import type { FormValues } from '../../../../../ContentState';

import translations from 'src/pages/LinkYourSoftware/.vocab';

interface Props {
  partner: Partner;
}

export const SolutionSelection = ({ partner }: Props) => {
  const { t } = useTranslations(translations);
  const isAuNz = useIsAuNz();

  const { fields } = useFieldArray<FormValues, 'solutions'>({
    name: 'solutions',
    rules: {
      validate: (value) => {
        if (value.filter(({ selected }) => selected).length === 0) {
          return t('selectAtLeastOneOption');
        }
        return true;
      },
    },
  });

  const { formState, clearErrors } = useFormContext<FormValues>();

  const { solutions } = useWatch<FormValues>();

  const selectedSolutionTypeCodes = solutions
    ? solutions.flatMap(({ typeCode, selected }) =>
        selected ? [typeCode as RelationshipTypeCode] : [],
      )
    : [];

  const solutionErrors = formState.errors.solutions;
  const selectedApplicationExport =
    selectedSolutionTypeCodes.includes('APPLICATION_EXPORT');
  const selectedApplyWithSeek = selectedSolutionTypeCodes.includes(
    'APPLICATION_PREFILL',
  );

  const getTooltipText = (typeCode: IntegrationRelationshipTypeEnum) => {
    switch (typeCode) {
      case 'APPLICATION_EXPORT':
        return t('optimisedApplyTooltip');
      case 'JOB_POSTING':
        return t('jobPostingTooltip');
      case 'APPLICATION_PREFILL':
        return t('applyWithSeekTooltip');
    }
  };

  return (
    <Box id="lys-solution-selection">
      <Stack space="gutter">
        <Heading level="3">{t('selectSeekSolutionsTitle')}</Heading>
        <Text>
          <Secondary>
            {t('selectSeekSolutions', {
              partner: partner.name,
              TextLink: (children) =>
                isAuNz ? (
                  <TextLink target="_blank" href="/partners/whats-possible/">
                    {children}
                  </TextLink>
                ) : null,
            })}
          </Secondary>
        </Text>
        <Stack space="medium">
          {fields.map(({ typeCode }, index) => {
            const solutionName = t(typeCode as RelationshipTypeCode);
            const disabled =
              (typeCode === 'APPLICATION_PREFILL' &&
                selectedApplicationExport) ||
              (typeCode === 'APPLICATION_EXPORT' && selectedApplyWithSeek);
            return solutionName ? (
              <Controller
                name={`solutions.${index}.selected`}
                key={`solutions.${index}.selected`}
                render={({ field, fieldState }) => (
                  <Inline space="small" alignY="center">
                    <Checkbox
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('solutions');
                      }}
                      disabled={disabled}
                      checked={field.value}
                      message={fieldState.error?.message}
                      tone={fieldState.error && 'critical'}
                      label={solutionName}
                      id={`checkbox-solution-${typeCode}`}
                      data={{ testid: `checkbox-solution-${index}` }}
                    />
                    <TooltipRenderer
                      id={`tooltip-solution-${index}`}
                      tooltip={
                        <Box padding="small">
                          <Text>{getTooltipText(typeCode)}</Text>
                        </Box>
                      }
                    >
                      {({ triggerProps }) => (
                        <Box aria-label="Help" {...triggerProps}>
                          <IconHelp size="small" />
                        </Box>
                      )}
                    </TooltipRenderer>
                  </Inline>
                )}
              />
            ) : null;
          })}
        </Stack>
        {solutionErrors ? (
          <Text
            id="solutions-message"
            icon={<IconCritical />}
            tone="critical"
            size="small"
          >
            {solutionErrors.root?.message}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
};
